<template>
  <div class="bg-gray-primary">
    <div class="w-full pb-12">
      <div class="space-y-10">
        <div class="w-full flex items-end justify-end">
          <button
            type="button"
            class="brand__button--trans-primary"
            @click="emitRouteChange"
          >
            <svg-icon
              aria-hidden="true"
              size="16"
              icon-name="arrow"
              class="rotate-90"
            />
            Back to My Board
          </button>
        </div>
        <div class="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3">
          <div class="px-4 sm:px-0">
            <h6>Profile</h6>
            <p class="text-sm mt-1">
              This information will be displayed publicly so be careful what you
              share.
            </p>
          </div>

          <div
            v-if="hasSubmitted"
            class="w-full flex flex-col space-y-6 px-4 py-6 col-span-2"
          >
            <div>
              {{ name }} has been
              {{ member ? 'updated' : 'added to your board' }}!
            </div>
            <div class="flex items-center justify-end gap-x-6 w-full px-4">
              <button
                type="button"
                class="brand__button--trans-primary"
                @click="emitRouteChange"
              >
                Go to My Board
              </button>
              <button
                type="button"
                class="brand__button--tertiary"
                @click="initRoute"
              >
                Add another member
              </button>
            </div>
          </div>
          <form
            v-else
            ref="form"
            class="bg-white shadow-sm sm:rounded-xl md:col-span-2"
            @submit.prevent="onSubmit"
          >
            <div class="px-4 py-6 sm:p-8">
              <div
                class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6"
              >
                <div class="sm:col-span-4">
                  <label
                    for="first-name"
                    class="block text-xs font-medium leading-6 text-gray-900"
                    >Full name (required)</label
                  >
                  <div class="mt-2">
                    <input
                      id="first-name"
                      v-model="name"
                      required
                      type="text"
                      name="first-name"
                      autocomplete="given-name"
                      class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-ces-blue sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div class="sm:col-span-6 flex flex-col gap-2">
                  <div class="flex items-center w-full">
                    <FormKit
                      v-model="logoInput"
                      type="file"
                      label="Profile image"
                      name="logoInput"
                      help="Upload your profile image"
                      accept=".jpg,.png"
                      no-files-icon="fileImage"
                      file-item-icon="fileImage"
                      file-remove-icon="close"
                      label-class="block text-xs font-medium leading-6 text-gray-900 mb-2"
                    />
                    <Logo
                      :is-school="false"
                      :src="logoSrc"
                      :show-placeholder="true"
                      wrapper-class="my-4 mx-8"
                    />
                  </div>
                </div>

                <div ref="roleParent" class="sm:col-span-4">
                  <label
                    for="boardRole"
                    class="block text-xs font-medium leading-6 text-gray-900"
                    >Role (required)</label
                  >
                  <div class="mt-2">
                    <select
                      id="boardRole"
                      v-model="boardRole"
                      required
                      name="boardRole"
                      class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-ces-blue sm:text-sm sm:leading-6"
                    >
                      <option disabled value="">Please select a role</option>
                      <option
                        v-for="role in BOARD_ROLES"
                        :key="role"
                        :value="role"
                      >
                        {{ role }}
                      </option>
                    </select>
                  </div>

                  <div
                    v-if="boardRole"
                    class="sm:col-span-4 mt-4 flex items-center"
                    :class="isSchoolAdmin ? 'cursor-not-allowed' : ''"
                    :title="
                      isSchoolAdmin
                        ? 'All admins can see public exluded files'
                        : null
                    "
                  >
                    <label
                      for="isFileLord"
                      class="block text-xs font-medium leading-6 text-gray-900"
                    >
                      Public Excluded File Access
                    </label>
                    <div class="ml-4">
                      <input
                        id="isFileLord"
                        v-model="isFileLord"
                        :disabled="isSchoolAdmin"
                        type="checkbox"
                        class="block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-ces-blue sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>

                  <div class="sm:col-span-4 mt-4 flex items-center">
                    <label
                      for="isSchoolAdmin"
                      class="block text-xs font-medium leading-6 text-gray-900"
                    >
                      Admin
                    </label>
                    <div class="ml-4">
                      <input
                        id="isSchoolAdmin"
                        v-model="isSchoolAdmin"
                        type="checkbox"
                        class="block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-ces-blue sm:text-sm sm:leading-6"
                      />
                    </div>
                  </div>
                </div>

                <div class="sm:col-span-4">
                  <label
                    for="email"
                    class="block text-xs font-medium leading-6 text-gray-900"
                    >Email (required)</label
                  >
                  <div class="mt-2">
                    <input
                      id="email"
                      v-model="email"
                      required
                      type="email"
                      name="email"
                      autocomplete="given-name"
                      class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-ces-blue sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div class="sm:col-span-3">
                  <label
                    for="phone"
                    class="block text-xs font-medium leading-6 text-gray-900"
                    >Mobile number</label
                  >
                  <div class="mt-2">
                    <input
                      id="phone"
                      v-model="mobile"
                      type="text"
                      name="phone"
                      autocomplete="phone"
                      class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-ces-blue sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>

                <div class="sm:col-span-3">
                  <label
                    for="phone"
                    class="block text-xs font-medium leading-6 text-gray-900"
                    >Phone number</label
                  >
                  <div class="mt-2">
                    <input
                      id="phone"
                      v-model="phone"
                      type="text"
                      name="phone"
                      autocomplete="phone"
                      class="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-ces-blue sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div
              class="flex items-center justify-end gap-x-4 border-t border-gray-900/10 px-4 py-4 sm:px-8"
            >
              <button
                type="button"
                class="brand__button--trans-primary"
                @click="reset"
              >
                Reset
              </button>
              <button
                type="submit"
                :disabled="!canSubmit"
                :class="[
                  canSubmit
                    ? 'brand__button--tertiary'
                    : 'brand__button--disabled',
                ]"
              >
                <div v-if="isSubmitting" class="h-6 w-6">
                  <Vue3Lottie :animation-data="ANIMATION_LOADING.DEFAULT" />
                </div>
                <span v-else>
                  {{ member ? 'Update' : 'Save' }}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { Vue3Lottie } from 'vue3-lottie'
import { ANIMATION_LOADING, BOARD_ROLES } from '../../constants.js'
import { useAutoAnimate } from '@formkit/auto-animate/vue'
import Logo from '../../components/Logo.vue'

export default defineComponent({
  components: {
    Vue3Lottie,
    Logo,
  },

  emits: ['emit-route-change'],

  setup(_props, { emit }) {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const isAdmin = computed(() => store.getters['auth/isAdmin'])

    const [roleParent] = useAutoAnimate()

    const emitRouteChange = () => {
      emit('emit-route-change', { primary: true, value: 'myBoard' })
    }

    const form = ref(null)
    const isSubmitting = ref(false)
    const hasSubmitted = ref(false)

    // Form
    const member = ref(null)
    const name = ref('')
    const logoInput = ref(null)
    const boardRole = ref('')
    const isFileLord = ref(false)
    const email = ref('')
    const mobile = ref('')
    const phone = ref('')
    const isSchoolAdmin = ref(false)

    const currentLogo = ref(null)
    const logo = computed(() => logoInput.value?.[0] || null)
    const logoFile = computed(() => logo.value?.file || null)
    const logoTitle = computed(() => logo.value?.name || null)
    const logoSrc = computed(() => {
      // If a new file is selected, preview that.
      if (logoFile.value) {
        return URL.createObjectURL(logoFile.value)
      }

      // Otherwise preview the existing image (or nothing if none exists).
      return typeof currentLogo.value === 'string' ? currentLogo.value : null
    })

    watch(boardRole, (newRole) => {
      // Only run this on new members
      if (!parseInt(route.query?.member)) {
        if (newRole === 'Staff' || newRole === 'Other') {
          isFileLord.value = false
        } else {
          isFileLord.value = true
        }
      }
    })

    watch(isSchoolAdmin, (newVal) => {
      if (newVal) {
        isFileLord.value = true
      }
    })

    const pageTitle = computed(() => {
      return `${member.value ? 'Update' : 'New'} Board Member`
    })

    const payload = computed(() => {
      const formData = new FormData()
      formData.append('name', name.value)
      formData.append('email', email.value)
      formData.append('board_role', boardRole.value)
      formData.append('tel', phone.value)
      formData.append('mobile', mobile.value)
      formData.append('is_portal_file_lord', isFileLord.value)
      formData.append('is_school_admin', isSchoolAdmin.value)
      if (logoFile.value) {
        formData.append('logo', logoFile.value)
        formData.append('logo_title', logoTitle.value)
      }
      if (member.value) {
        formData.append('id', member.value.id)
      }

      return formData
    })

    const canSubmit = computed(() => {
      let verified =
        !hasSubmitted.value &&
        !isSubmitting.value &&
        name.value &&
        boardRole.value &&
        email.value
      if (member.value) {
        // updating existing member: confirm at least one field has been updated
        const hasBeenUpdated =
          member.value.name !== name.value ||
          member.value.boardRole !== boardRole.value ||
          member.value.email !== email.value ||
          member.value.mobile !== mobile.value ||
          member.value.phone !== phone.value ||
          member.value.isFileLord !== isFileLord.value
        member.value.isSchoolAdmin !== isSchoolAdmin.value
        verified = verified && hasBeenUpdated
      }

      // creating a new member
      return verified
    })

    const initRoute = () => {
      callFreshForm()
      // Clear member params!
      router.push({ name: route.name, params: { id: route.params.id } })
    }

    const callFreshForm = () => {
      init(true)
    }

    const reset = () => {
      init()
    }

    const onSubmit = async () => {
      isSubmitting.value = true
      const response = await store.dispatch(
        'portal/dispatchSetMember',
        payload.value,
      )
      if (response) {
        hasSubmitted.value = true
      }
      isSubmitting.value = false
    }

    const init = (callFresh = false) => {
      if (member.value && !callFresh) {
        name.value = member.value.name
        currentLogo.value = member.value.logo
        boardRole.value = member.value.boardRole
        email.value = member.value.email
        mobile.value = member.value.mobile
        phone.value = member.value.phone
        isFileLord.value = Boolean(member.value.isFileLord)
        isSchoolAdmin.value = member.value.role === 2
      } else {
        name.value = ''
        currentLogo.value = null
        boardRole.value = ''
        email.value = ''
        mobile.value = ''
        phone.value = ''
        isFileLord.value = false
        isSchoolAdmin.value = false
      }

      hasSubmitted.value = false
    }

    onMounted(() => {
      if (!isAdmin.value) {
        router.push({ name: 'dashboard', params: { id: route.params.id } })
      }
      // Go and fetch the (existing) member
      const memberId = parseInt(route.query?.member)
      if (memberId) {
        // we are editing an existing user!
        member.value = store.getters['portal/getMember'](memberId)
        if (!member.value) {
          // if we can't find that member, refresh/reset the form
          callFreshForm(true)
        }
      } else {
        // we are creating a new user!
        member.value = null
      }

      init()
    })

    return {
      ANIMATION_LOADING,
      BOARD_ROLES,
      route,
      roleParent,
      isAdmin,
      emitRouteChange,
      form,
      pageTitle,
      member,
      name,
      logoInput,
      logoSrc,
      boardRole,
      isFileLord,
      isSchoolAdmin,
      email,
      phone,
      mobile,
      canSubmit,
      isSubmitting,
      hasSubmitted,
      initRoute,
      callFreshForm,
      reset,
      onSubmit,
    }
  },
})
</script>

<style scoped>
.cursor-not-allowed * {
  cursor: not-allowed !important;
}
</style>
